/** @jsx jsx */
import { jsx, css } from '@emotion/core';
import { useState, useMemo, useEffect } from 'react';
import actionOnClick from '../../utils/actionOnClick';

const supportedButtons = {
  postback: true,
  web_url: true,
  phone_number: true,
  webview: true,
  undefined: false,
};

// Simple arrow component
const ArrowIcon = ({ isOpen, ...props }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 0.2s ease',
    }}
    {...props}
  >
    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ButtonTemplate = ({ payload = {}, onButtonClick, config }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const buttons = payload.buttons || [];
  const shouldShowDropdown = buttons.length > 0;

  // Calculate dynamic width based on longest button text, but respect parent container
  const dynamicWidth = useMemo(() => {
    if (buttons.length === 0) return 'auto';

    const maxButtonLength = Math.max(...buttons.map(button => button.title?.length || 0));
    const minWidth = 200; // Minimum width
    const maxWidth = 400; // Maximum width
    const calculatedWidth = Math.max(minWidth, Math.min(maxWidth, maxButtonLength * 8 + 40));

    // Use max-width instead of fixed width to prevent overflow
    return `max-width: ${calculatedWidth}px; width: 100%;`;
  }, [buttons]);

  const handleTitleClick = () => {
    if (shouldShowDropdown) {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <div
      css={theme => css`
        color: ${theme.chat.agent.color};
        border: solid 1px ${theme.chat.agent.color};
        background-color: ${theme.chat.agent.textColor};
        display: flex;
        flex-direction: column;
        margin: 0 2em 0 2rem;
        margin-bottom: 1rem;
        min-height: max-content;
        border-radius: 5px;
        overflow: hidden;
        box-shadow: 2px 2px 4px 0 rgba(101, 82, 82, 0.07);
        flex-shrink: 0;
        ${dynamicWidth};
      `}
    >
      {/* Clickable title header */}
      <div
        onClick={handleTitleClick}
        css={theme => css`
          text-align: center;
          padding: 0.5rem;
          font-weight: 500;
          padding: 7px 0 7px 0;
          color: ${theme.chat.agent.color};
          cursor: ${shouldShowDropdown ? 'pointer' : 'default'};
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          ${shouldShowDropdown &&
          `
            &:hover {
              background-color: ${theme.chat.agent.color}20;
            }
          `}
        `}
      >
        <span>{payload.text}</span>
        {shouldShowDropdown && <ArrowIcon isOpen={isExpanded} />}
      </div>

      {/* Dropdown content */}
      {isExpanded && shouldShowDropdown && (
        <div>
          {buttons.map((button, index) => (
            <div
              title={!supportedButtons[button.type] ? `${button.type} is not supported by Interact` : ''}
              onClick={() => actionOnClick(onButtonClick, button, config)}
              key={index}
              css={theme => css`
                border-top: ${theme.chat.agent.textColor} solid 1px;
                text-align: center;
                cursor: pointer;
                padding: 8px 12px;
                color: ${theme.chat.agent.textColor};
                background-color: ${theme.chat.agent.color};
                word-wrap: break-word;
                word-break: break-word;
                overflow-wrap: break-word;
                white-space: normal;
                line-height: 1.4;
                &:hover {
                  text-decoration: underline;
                }
              `}
            >
              {button.title}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ButtonTemplate;
